import React, { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AIGuideProvider } from './context/AIGuideContext';
import { SharedAIProvider } from './context/SharedAIContext';
import { Toaster } from './components/ui/sonner';
import { BackToStart } from './components/brand/BackToStart';
import { OverflowMenu } from './components/brand/OverflowMenu';
import { LogoutButton } from './components/brand/LogoutButton';
import AICompanion from './components/AICompanion';
import TestimonialPrompt from './components/TestimonialPrompt';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import { ExitSurveyModal, useExitSurvey } from './components/ExitSurveyModal';

// Pages
import Landing from './pages/Landing';
import Auth from './pages/Auth';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Regulate from './pages/Regulate';
import Communicate from './pages/Communicate';
import History from './pages/History';
import Settings from './pages/Settings';
import Account from './pages/Account';
import BrandShowcase from './pages/BrandShowcase';
import ComponentLibrary from './pages/ComponentLibrary';
import Paywall from './pages/Paywall';
import BetaSignup from './pages/BetaSignup';
import BetaFeedbackWidget from './components/BetaFeedbackWidget';

// Regulation Suite Pages
import RegulationHome from './pages/RegulationHome';
import RegulationNavigator from './pages/RegulationNavigator';
import MicroReset from './pages/MicroReset';
import SelfTalkReframes from './pages/SelfTalkReframes';
import CoRegulationSimple from './pages/CoRegulationSimple';
import SavedFavourites from './pages/SavedFavourites';
import RelationshipSafetyCheck from './pages/RelationshipSafetyCheck';
import ADHDRelationalTriggers from './pages/ADHDRelationalTriggers';
import WhatDoWeNeed from './pages/WhatDoWeNeed';
import PartnerMode from './pages/PartnerMode';
import ADHDPartnerSupport from './pages/ADHDPartnerSupport';
import PaymentSuccess from './pages/PaymentSuccess';
import CheckoutPage from './pages/CheckoutPage';

// New Body & Audio Tools
import BodyScan from './pages/BodyScan';
import ProgressiveMuscleRelaxation from './pages/ProgressiveMuscleRelaxation';
import SomaticExperiencing from './pages/SomaticExperiencing';
import GuidedMeditations from './pages/GuidedMeditations';
import BreathingPatterns from './pages/BreathingPatterns';
import CrisisResources from './pages/CrisisResources';
import EMDR from './pages/EMDR';

// Relationship Hub Pages
import RelationshipHub from './pages/RelationshipHub';
import WhichCycleAreWeIn from './pages/WhichCycleAreWeIn';
import InfinityLoop from './pages/InfinityLoop';
import SoloWhatDoINeed from './pages/SoloWhatDoINeed';
import AttachmentNeeds from './pages/AttachmentNeeds';
import BreakTheCycle from './pages/BreakTheCycle';
import RelationshipCheckIn from './pages/RelationshipCheckIn';
import RepairReconnection from './pages/RepairReconnection';
import AIGuide from './pages/AIGuide';
import BoxBreathing from './pages/BoxBreathing';
import InitialAssessment from './pages/InitialAssessment';
import BreathingOrbMeditation from './pages/BreathingOrbMeditation';
import StillSphere from './pages/StillSphere';
import EmotionIdentifier from './pages/EmotionIdentifier';
import Impact from './pages/Impact';
import BlogIndex from './pages/Blog';
import FAQ from './pages/FAQ';
import ADHDRelationships from './pages/ADHDRelationships';

// SEO Landing Pages
import SEOLandingEmotionalRegulation from './pages/SEOLandingEmotionalRegulation';
import SEOLandingRelationshipTools from './pages/SEOLandingRelationshipTools';
import SEOLandingGroundingTechniques from './pages/SEOLandingGroundingTechniques';
import PromoLanding from './pages/PromoLanding';
import PromoShareImage from './pages/PromoShareImage';
import PromoAssets from './pages/PromoAssets';
import BackgroundPreview from './pages/BackgroundPreview';
import NoIndex from './components/SEO/NoIndex';

// Partner & Crisis Pages
import ConflictCrisis from './pages/ConflictCrisis';
import { CreatePartnerInvite, AcceptPartnerInvite, PartnerWelcome } from './pages/PartnerInvite';

// Support Pages
import Support from './pages/Support';
import Contact from './pages/Contact';

// Public SEO Pages
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import HowItWorks from './pages/HowItWorks';
import About from './pages/About';
import Tools from './pages/Tools';
import PublicSupport from './pages/PublicSupport';

// Admin Pages
import ToolEditor from './pages/admin/ToolEditor';

// Unified Home - Context-Aware Landing
import UnifiedHome from './pages/UnifiedHome';

// Understanding ADHD - Educational content page
import UnderstandingADHD from './pages/UnderstandingADHD';

// Hypnotherapy - Educational content and enquiry page
import Hypnotherapy from './pages/Hypnotherapy';

// Protected Route Component (with minimal navigation - just overflow menu)
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading, user } = useAuth();
    const [showTestimonialPrompt, setShowTestimonialPrompt] = useState(false);
    const [userTier, setUserTier] = useState('free');
    const { showSurvey, daysRemaining, closeSurvey } = useExitSurvey();
    
    // Check if user should see testimonial prompt - use sessionStorage to track across navigations
    useEffect(() => {
        const checkTestimonialPrompt = async () => {
            // Only check once per session
            if (loading || !isAuthenticated) return;
            
            const alreadyChecked = sessionStorage.getItem('testimonial_prompt_checked');
            if (alreadyChecked) return;
            
            // Mark as checked for this session
            sessionStorage.setItem('testimonial_prompt_checked', 'true');
            
            try {
                const token = localStorage.getItem('co_regulate_token');
                if (!token) return;
                
                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/testimonials/check-prompt`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                
                if (response.ok) {
                    const data = await response.json();
                    if (data.should_show_prompt) {
                        setUserTier(data.user_tier);
                        // Small delay to not interrupt initial page load
                        setTimeout(() => setShowTestimonialPrompt(true), 3000);
                    }
                }
            } catch (err) {
                console.error('Failed to check testimonial prompt:', err);
            }
        };
        
        checkTestimonialPrompt();
    }, [isAuthenticated, loading]);
    
    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="w-3 h-3 rounded-full bg-primary animate-breathe" />
            </div>
        );
    }
    
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }
    
    // Minimal navigation - just overflow menu for clean, calm pages
    return (
        <>
            <NoIndex />
            <OverflowMenu />
            {children}
            <TestimonialPrompt 
                isOpen={showTestimonialPrompt} 
                onClose={() => setShowTestimonialPrompt(false)}
                userTier={userTier}
            />
            <ExitSurveyModal
                isOpen={showSurvey}
                onClose={closeSurvey}
                daysRemaining={daysRemaining}
            />
        </>
    );
};

// Public Route (redirects to home if authenticated)
const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="w-3 h-3 rounded-full bg-primary animate-breathe" />
            </div>
        );
    }
    
    if (isAuthenticated) {
        return <Navigate to="/home" replace />;
    }
    
    // Public auth routes (login, signup, forgot-password) should not be indexed
    return (
        <>
            <NoIndex />
            {children}
        </>
    );
};

// Root Route - Show landing page for unauthenticated users, dashboard for authenticated
const RootRoute = () => {
    const { isAuthenticated, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="w-3 h-3 rounded-full bg-primary animate-breathe" />
            </div>
        );
    }
    
    // Authenticated users go to dashboard
    if (isAuthenticated) {
        return <Navigate to="/home" replace />;
    }
    
    // Unauthenticated users see the marketing landing page
    return <Landing />;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Root - Smart redirect based on auth status */}
            <Route path="/" element={<RootRoute />} />
            
            {/* Legacy settle and body-checkin routes - redirect to home */}
            <Route path="/settle" element={<Navigate to="/home" replace />} />
            <Route path="/body-checkin" element={<Navigate to="/home" replace />} />
            
            {/* Landing/Marketing Page - accessible directly */}
            <Route path="/landing" element={<Landing />} />
            
            {/* Blog - Public, SEO-optimized content */}
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/:slug" element={<BlogIndex />} />
            
            {/* SEO Landing Pages - Public, targeted for specific search terms */}
            <Route path="/adhd-emotional-regulation" element={<SEOLandingEmotionalRegulation />} />
            <Route path="/adhd-relationship-tools" element={<SEOLandingRelationshipTools />} />
            <Route path="/adhd-grounding-techniques" element={<SEOLandingGroundingTechniques />} />
            <Route path="/coregulater" element={<PromoLanding />} />
            <Route path="/join" element={<PromoLanding />} />
            <Route path="/promo-image" element={<PromoShareImage />} />
            <Route path="/promo-assets" element={<PromoAssets />} />
            <Route path="/background-preview" element={<BackgroundPreview />} />
            <Route path="/press-kit" element={<Navigate to="/promo-assets" replace />} />
            
            {/* Legacy auth routes - redirect to /auth */}
            <Route path="/login" element={<Navigate to="/auth?mode=login" replace />} />
            <Route path="/register" element={<Navigate to="/auth" replace />} />
            
            {/* Legacy onboarding route - redirect to assessment */}
            <Route path="/onboarding" element={<Navigate to="/initial-assessment" replace />} />
            
            {/* Protected Routes */}
            <Route path="/home" element={
                <ProtectedRoute>
                    <UnifiedHome />
                </ProtectedRoute>
            } />
            {/* Welcome/Landing page - public marketing page */}
            <Route path="/welcome" element={<Landing />} />
            {/* Legacy dashboard redirect */}
            <Route path="/dashboard" element={<Navigate to="/home" replace />} />
            
            {/* Understanding ADHD - Educational Content */}
            <Route path="/understanding-adhd" element={
                <ProtectedRoute>
                    <UnderstandingADHD />
                </ProtectedRoute>
            } />

            {/* Hypnotherapy educational page */}
            <Route path="/hypnotherapy" element={
                <ProtectedRoute>
                    <Hypnotherapy />
                </ProtectedRoute>
            } />
            
            <Route path="/regulate" element={
                <ProtectedRoute>
                    <RegulationHome />
                </ProtectedRoute>
            } />
            <Route path="/regulate/regulation-navigator" element={
                <ProtectedRoute>
                    <RegulationNavigator />
                </ProtectedRoute>
            } />
            {/* Legacy redirect */}
            <Route path="/regulate/overwhelm-navigator" element={<Navigate to="/regulate/regulation-navigator" replace />} />
            <Route path="/regulate/micro-reset" element={
                <ProtectedRoute>
                    <MicroReset />
                </ProtectedRoute>
            } />
            <Route path="/regulate/butterfly-meditation" element={
                <ProtectedRoute>
                    <BreathingOrbMeditation />
                </ProtectedRoute>
            } />
            <Route path="/regulate/breathing-orb" element={
                <ProtectedRoute>
                    <BreathingOrbMeditation />
                </ProtectedRoute>
            } />
            <Route path="/regulate/still-sphere" element={
                <ProtectedRoute>
                    <StillSphere />
                </ProtectedRoute>
            } />
            <Route path="/regulate/emotion-identifier" element={
                <ProtectedRoute>
                    <EmotionIdentifier />
                </ProtectedRoute>
            } />
            <Route path="/regulate/self-talk" element={
                <ProtectedRoute>
                    <SelfTalkReframes />
                </ProtectedRoute>
            } />
            {/* Co-Regulation Tool - Simple working version */}
            <Route path="/regulate/coregulation" element={
                <ProtectedRoute>
                    <CoRegulationSimple />
                </ProtectedRoute>
            } />
            <Route path="/regulate/saved" element={
                <ProtectedRoute>
                    <SavedFavourites />
                </ProtectedRoute>
            } />
            <Route path="/regulate/relationship-check" element={
                <ProtectedRoute>
                    <RelationshipSafetyCheck />
                </ProtectedRoute>
            } />
            {/* Redirect duplicate relationship-cycles to the main one */}
            <Route path="/regulate/relationship-cycles" element={<Navigate to="/relationship-hub/cycles" replace />} />
            {/* Redirect adhd-triggers to the consolidated ADHD & Relationships page */}
            <Route path="/regulate/adhd-triggers" element={<Navigate to="/adhd-relationships" replace />} />
            <Route path="/regulate/what-do-we-need" element={
                <ProtectedRoute>
                    <WhatDoWeNeed />
                </ProtectedRoute>
            } />
            <Route path="/regulate/partner-mode" element={
                <ProtectedRoute>
                    <PartnerMode />
                </ProtectedRoute>
            } />
            <Route path="/regulate/adhd-partner-support" element={
                <ProtectedRoute>
                    <ADHDPartnerSupport />
                </ProtectedRoute>
            } />
            
            {/* Relationship Hub Routes */}
            <Route path="/relationship-hub" element={
                <ProtectedRoute>
                    <RelationshipHub />
                </ProtectedRoute>
            } />
            <Route path="/relationship-hub/cycles" element={
                <ProtectedRoute>
                    <WhichCycleAreWeIn />
                </ProtectedRoute>
            } />
            <Route path="/relationship-hub/solo-need" element={
                <ProtectedRoute>
                    <SoloWhatDoINeed />
                </ProtectedRoute>
            } />
            <Route path="/relationship-hub/attachment-needs" element={
                <ProtectedRoute>
                    <AttachmentNeeds />
                </ProtectedRoute>
            } />
            <Route path="/relationship-hub/break-the-cycle" element={
                <ProtectedRoute>
                    <BreakTheCycle />
                </ProtectedRoute>
            } />
            <Route path="/relationship-hub/check-in" element={
                <ProtectedRoute>
                    <RelationshipCheckIn />
                </ProtectedRoute>
            } />
            <Route path="/relationship-hub/repair" element={
                <ProtectedRoute>
                    <RepairReconnection />
                </ProtectedRoute>
            } />
            <Route path="/relationship-hub/infinity-loop" element={
                <ProtectedRoute>
                    <InfinityLoop />
                </ProtectedRoute>
            } />
            <Route path="/adhd-relationships" element={
                <ProtectedRoute>
                    <ADHDRelationships />
                </ProtectedRoute>
            } />
            <Route path="/guide" element={
                <ProtectedRoute>
                    <AIGuide />
                </ProtectedRoute>
            } />
            <Route path="/regulate/box-breathing" element={
                <ProtectedRoute>
                    <BoxBreathing />
                </ProtectedRoute>
            } />
            
            {/* New Body & Audio Tools */}
            <Route path="/regulate/body-scan" element={
                <ProtectedRoute>
                    <BodyScan />
                </ProtectedRoute>
            } />
            <Route path="/regulate/pmr" element={
                <ProtectedRoute>
                    <ProgressiveMuscleRelaxation />
                </ProtectedRoute>
            } />
            <Route path="/regulate/somatic" element={
                <ProtectedRoute>
                    <SomaticExperiencing />
                </ProtectedRoute>
            } />
            <Route path="/regulate/guided-meditations" element={
                <ProtectedRoute>
                    <GuidedMeditations />
                </ProtectedRoute>
            } />
            <Route path="/regulate/breathing-patterns" element={
                <ProtectedRoute>
                    <BreathingPatterns />
                </ProtectedRoute>
            } />
            <Route path="/regulate/emdr" element={
                <ProtectedRoute>
                    <EMDR />
                </ProtectedRoute>
            } />
            <Route path="/crisis" element={<CrisisResources />} />
            
            {/* Legacy routes - redirect old paths */}
            <Route path="/relational-hub" element={<Navigate to="/relationship-hub" replace />} />
            <Route path="/relational-hub/*" element={<Navigate to="/relationship-hub" replace />} />
            <Route path="/relationship-check-in" element={<Navigate to="/relationship-hub/check-in" replace />} />
            
            {/* Initial Assessment is accessible to everyone (registered and unregistered) */}
            <Route path="/initial-assessment" element={<InitialAssessment />} />
            
            {/* Legacy butterfly-meditation route - redirect to breathing orb */}
            <Route path="/butterfly-meditation" element={<Navigate to="/regulate/breathing-orb" replace />} />
            
            <Route path="/regulate/legacy" element={
                <ProtectedRoute>
                    <Regulate />
                </ProtectedRoute>
            } />
            {/* Legacy coregulate route - use simple version */}
            <Route path="/coregulate" element={
                <ProtectedRoute>
                    <CoRegulationSimple />
                </ProtectedRoute>
            } />
            <Route path="/communicate" element={
                <ProtectedRoute>
                    <Communicate />
                </ProtectedRoute>
            } />
            <Route path="/history" element={
                <ProtectedRoute>
                    <History />
                </ProtectedRoute>
            } />
            <Route path="/settings" element={
                <ProtectedRoute>
                    <Settings />
                </ProtectedRoute>
            } />
            <Route path="/account" element={
                <ProtectedRoute>
                    <Account />
                </ProtectedRoute>
            } />
            
            {/* Admin Routes */}
            <Route path="/admin/tools" element={
                <ProtectedRoute>
                    <ToolEditor />
                </ProtectedRoute>
            } />
            
            {/* Partner Invite Routes */}
            <Route path="/partner/invite" element={
                <ProtectedRoute>
                    <CreatePartnerInvite />
                </ProtectedRoute>
            } />
            <Route path="/partner-invite/:inviteCode" element={<AcceptPartnerInvite />} />
            <Route path="/partner-welcome" element={
                <ProtectedRoute>
                    <PartnerWelcome />
                </ProtectedRoute>
            } />
            
            {/* Conflict Crisis Mode */}
            <Route path="/crisis" element={
                <ProtectedRoute>
                    <ConflictCrisis />
                </ProtectedRoute>
            } />
            
            {/* Brand Assets (Public) */}
            <Route path="/brand" element={<BrandShowcase />} />
            <Route path="/components" element={<ComponentLibrary />} />
            <Route path="/pricing" element={<Paywall />} />
            <Route path="/beta" element={<BetaSignup />} />
            <Route path="/try-free" element={<BetaSignup />} />
            <Route path="/impact" element={<Impact />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/contact" element={<Contact />} />
            
            {/* Public SEO Pages */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/about" element={<About />} />
            <Route path="/tools" element={<Tools />} />
            <Route path="/help" element={<PublicSupport />} />
            
            {/* Support - Protected */}
            <Route path="/support" element={
                <ProtectedRoute>
                    <Support />
                </ProtectedRoute>
            } />
            
            {/* Payment Routes */}
            <Route path="/checkout" element={
                <ProtectedRoute>
                    <CheckoutPage />
                </ProtectedRoute>
            } />
            <Route path="/payment/success" element={
                <ProtectedRoute>
                    <PaymentSuccess />
                </ProtectedRoute>
            } />
            
            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <AIGuideProvider>
                    <SharedAIProvider>
                        <div className="App">
                            <AppRoutes />
                            {/* Global AI Companion - available on all authenticated pages */}
                            <AICompanion variant="floating" />
                            {/* Beta Feedback Widget - only shows for beta testers */}
                            <BetaFeedbackWidget />
                            {/* PWA Install Prompt */}
                            <PWAInstallPrompt />
                            <Toaster 
                                position="top-center"
                                toastOptions={{
                                    style: {
                                        background: 'hsl(var(--card))',
                                        border: '1px solid hsl(var(--border))',
                                        color: 'hsl(var(--foreground))',
                                    },
                                }}
                            />
                        </div>
                    </SharedAIProvider>
                </AIGuideProvider>
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
